<template>
  <!-- 창이 좁거나 컨텐츠가 길면 가로 스크롤 표시 -->
  <header
    class="z-sticky fixed left-0 right-0 top-0 overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-gutter:stable]"
    :class="headerBgClass"
  >
    <!-- 내용이 뷰포트보다 넓으면 이 헤더에 가로 스크롤(스크롤바 노출). 내부는 최소 100%·필요 시 max-content로 확장 -->
    <div
      class="header-container gap-x-padding-36 px-padding-36 mx-auto box-border flex h-[64px] w-max min-w-full items-center justify-between py-[9px]"
    >
      <div class="header-left flex shrink-0 items-center">
        <div class="mr-9 h-10 w-auto shrink-0">
          <BaseLogoModa :size="40" home-path="/order" />
        </div>
        <template v-if="isLoggedIn">
          <!-- 로그인 상태: 1650px 이상 가로 GNB, 미만 햄버거 메뉴 -->
          <div class="hidden min-[1650px]:block">
            <NavBar />
          </div>
          <div class="shrink-0 min-[1650px]:hidden">
            <BaseMenu
              :items="hamburgerMenuItems"
              align="left"
              items-class="text-default"
              @select="handleHamburgerSelect"
            >
              <template #trigger>
                <BaseIconButton
                  color="white"
                  :icon="{ name: 'grab' }"
                  backgroundColor="transparent"
                  shape="square"
                  ariaLabel="주요 메뉴 열기"
                />
              </template>
            </BaseMenu>
          </div>
        </template>
        <div v-else>
          <NavBar />
        </div>
      </div>

      <div class="header-right flex shrink-0 items-center">
        <StatusBarArea v-if="isLoggedIn" />
        <LoginButtonArea v-else />
        <slot name="header-right" />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { DESKTOP_NAV_MENUS, type DesktopNavMenuItem } from '@/constants/desktopNavMenus';
import LoginButtonArea from '@/components/layout/fragments/LoginButtonArea.vue';
import StatusBarArea from '@/components/layout/fragments/StatusBarArea.vue';
import { BaseIconButton, BaseLogoModa, BaseMenu } from '@template/ui';
import NavBar from '@/components/layout/fragments/NavBar.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useUserStore } from '@/stores/useUserStore';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

const userStore = useUserStore();
const { isLoggedIn } = storeToRefs(userStore);

const route = useRoute();
const router = useRouter();

const hamburgerMenuItems = computed(() =>
  DESKTOP_NAV_MENUS.map((menu) => ({ label: menu.text, value: menu }))
);

/**
 * 1920px 미만 햄버거 메뉴에서 항목 선택 시 라우팅
 */
const handleHamburgerSelect = (item: { label: string; value?: DesktopNavMenuItem }) => {
  const menu = item.value;
  if (!menu) return;
  if (menu.params) {
    router.push({ name: menu.to, params: menu.params });
  } else {
    router.push({ name: menu.to });
  }
};
const isOverlay = computed(() => route.meta?.headerOverlay === true);

const isScrolled = ref(false);
const heroThreshold = ref<number | null>(null);

const handleScroll = () => {
  // 홈(overlay)에서는 Hero 높이 전까지 투명 유지
  const threshold = heroThreshold.value ?? 20;
  isScrolled.value = window.scrollY > threshold;
};

const measureHeroThreshold = () => {
  if (!isOverlay.value) {
    heroThreshold.value = null;
    return;
  }

  const heroEl = document.querySelector<HTMLElement>('[data-landing-hero]');
  if (!heroEl) {
    heroThreshold.value = 20;
    return;
  }

  // Hero 영역이 끝나는 지점까지 투명 유지 (헤더 높이만큼은 제외)
  const headerHeight = 64;
  heroThreshold.value = Math.max(0, heroEl.offsetHeight - headerHeight);
};

onMounted(() => {
  userStore.updateLoginStatus();
  measureHeroThreshold();
  handleScroll();
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', measureHeroThreshold, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll as any);
  window.removeEventListener('resize', measureHeroThreshold as any);
});

const headerBgClass = computed(() => {
  if (!isOverlay.value) return 'bg-black';
  // 홈에서만: 상단 투명, 스크롤 시 검정 + 블러
  return isScrolled.value ? 'bg-black/85 backdrop-blur-md' : 'bg-transparent';
});
</script>
